import {
  BookOutlined,
  BulbOutlined,
  ExperimentOutlined,
  FileTextOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  TagOutlined,
} from "@ant-design/icons";
import { Column, Line } from "@ant-design/plots";
import { Card, DatePicker, Empty, Spin, Tooltip, Typography } from "antd";
import PageLayout from "components/common/pageLayout/pageLayout";
import moment from "moment";
import { useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useGetAnalyticsPerformanceQuery } from "../api";
import { ANALYTICS_MOCK } from "../mockData";
import { ANALYTICS_USE_MOCK_DATA } from "utils/featureFlags";
import AnalyticsKPIStrip from "./AnalyticsKPIStrip";
import AnalyticsTable from "./AnalyticsTable";
import styles from "./AnalyticsPage.module.less";

const { RangePicker } = DatePicker;
const { Title, Text } = Typography;

const PASS_THRESHOLD = 60;

const ATTRIBUTE_CONFIG = {
  specialty: {
    label: "Specialty",
    plural: "Specialties",
    color: "#4b93ff",
    icon: <ExperimentOutlined />,
    description: "Performance breakdown grouped by medical specialty",
  },
  script: {
    label: "Script",
    plural: "Scripts",
    color: "#8b5cf6",
    icon: <FileTextOutlined />,
    description: "Performance breakdown grouped by learning script",
  },
  topic: {
    label: "Topic",
    plural: "Topics",
    color: "#16a34a",
    icon: <TagOutlined />,
    description: "Performance breakdown grouped by topic area",
  },
  explanation: {
    label: "Explanation",
    plural: "Explanations",
    color: "#d97706",
    icon: <BookOutlined />,
    description: "Performance breakdown grouped by explanation type",
  },
};

const DATE_PRESETS = [
  { label: "Last 30 days", value: [moment().subtract(30, "days"), moment()] },
  { label: "Last 3 months", value: [moment().subtract(3, "months"), moment()] },
  { label: "Last 6 months", value: [moment().subtract(6, "months"), moment()] },
  { label: "This year", value: [moment().startOf("year"), moment()] },
  { label: "All time", value: [moment("2020-01-01"), moment()] },
];

const buildBarData = (items) =>
  [...items]
    .sort((a, b) => b.accuracy - a.accuracy)
    .map((item) => ({ name: item.name, accuracy: item.accuracy }));

const AnalyticsPage = ({ attribute = "specialty" }) => {
  const user   = useSelector((state) => state?.app?.user?.info);
  const userId = user?.user_code ?? user?._id;
  const config = ATTRIBUTE_CONFIG[attribute] ?? ATTRIBUTE_CONFIG.specialty;

  const [dateRange, setDateRange] = useState(null);

  const queryBody = useMemo(() => {
    const body = { attribute, userId };
    if (dateRange?.[0]) body.startDate = dateRange[0].format("YYYY-MM-DD");
    if (dateRange?.[1]) body.endDate   = dateRange[1].format("YYYY-MM-DD");
    return body;
  }, [attribute, userId, dateRange]);

  const { data: apiData, isLoading, isFetching } = useGetAnalyticsPerformanceQuery(
    queryBody,
    { skip: ANALYTICS_USE_MOCK_DATA || !userId, refetchOnMountOrArgChange: true }
  );

  const data = ANALYTICS_USE_MOCK_DATA ? ANALYTICS_MOCK[attribute] : apiData;
  const loading = ANALYTICS_USE_MOCK_DATA ? false : isLoading || isFetching;

  const barData   = useMemo(() => buildBarData(data?.items ?? []), [data]);
  const trendData = data?.trendOverTime ?? [];

  // Bar (Column) chart config
  const barConfig = useMemo(() => ({
    data: barData,
    xField: "name",
    yField: "accuracy",
    color: ({ accuracy }) =>
      accuracy >= 80 ? "#16a34a" :
      accuracy >= 60 ? "#4b93ff" :
      accuracy >= 40 ? "#d97706" : "#ef4444",
    label: {
      position: "top",
      formatter: (d) => `${d.accuracy}%`,
      style: { fontSize: 11, fontWeight: 600 },
    },
    annotations: [
      {
        type: "line",
        start: ["min", PASS_THRESHOLD],
        end:   ["max", PASS_THRESHOLD],
        style: { stroke: "#faad14", lineDash: [4, 4], lineWidth: 1.5 },
        text: {
          content: `Pass ${PASS_THRESHOLD}%`,
          offsetY: -6,
          style: { fontSize: 11, fill: "#faad14" },
        },
      },
    ],
    yAxis: { min: 0, max: 100, title: { text: "Accuracy (%)" } },
    xAxis: { label: { autoRotate: true, style: { fontSize: 11 } } },
    tooltip: {
      formatter: (d) => ({ name: "Accuracy", value: `${d.accuracy}%` }),
    },
    columnStyle: { radius: [4, 4, 0, 0] },
    padding: "auto",
    autoFit: true,
  }), [barData]);

  // Trend line chart config
  const trendConfig = useMemo(() => ({
    data: trendData,
    xField: "month",
    yField: "avgAccuracy",
    smooth: true,
    color: config.color,
    point: {
      size: 5,
      shape: "circle",
      style: { fill: config.color, stroke: "#fff", lineWidth: 2 },
    },
    annotations: [
      {
        type: "line",
        start: ["min", PASS_THRESHOLD],
        end:   ["max", PASS_THRESHOLD],
        style: { stroke: "#faad14", lineDash: [4, 4], lineWidth: 1.5 },
        text: {
          content: `Pass ${PASS_THRESHOLD}%`,
          offsetY: -6,
          style: { fontSize: 11, fill: "#faad14" },
        },
      },
    ],
    yAxis: { min: 0, max: 100, title: { text: "Avg Accuracy (%)" } },
    xAxis: { label: { autoRotate: true, style: { fontSize: 11 } } },
    tooltip: {
      formatter: (d) => ({ name: "Avg Accuracy", value: `${d.avgAccuracy}%` }),
    },
    area: {
      style: {
        fill: `l(270) 0:${config.color}00 1:${config.color}33`,
      },
    },
    autoFit: true,
    padding: "auto",
  }), [trendData, config.color]);

  const hasData = (data?.items?.length ?? 0) > 0;

  return (
    <PageLayout>
      <div className={styles.analyticsPage}>
        {/* ── Page header ─────────────────────────────────────────────── */}
        <div className={styles.pageHeader}>
          <div className={styles.headerLeft}>
            <div className={styles.headerIcon} style={{ color: config.color }}>
              {config.icon}
            </div>
            <div>
              <Title level={4} className={styles.pageTitle}>
                Analytics by {config.label}
              </Title>
              <Text type="secondary" className={styles.pageDesc}>
                {config.description}
              </Text>
            </div>
          </div>

          <div className={styles.headerRight}>
            <FilterOutlined className={styles.filterIcon} />
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              presets={DATE_PRESETS}
              format="DD MMM YYYY"
              allowClear
              placeholder={["Start date", "End date"]}
              style={{ minWidth: 240 }}
            />
            {dateRange && (
              <Tooltip title="Showing filtered data">
                <InfoCircleOutlined className={styles.activeFilterDot} />
              </Tooltip>
            )}
          </div>
        </div>

        {/* ── KPI Strip ───────────────────────────────────────────────── */}
        <AnalyticsKPIStrip
          kpis={data?.kpis}
          isLoading={loading}
          attributeLabel={config.label}
        />

        {loading ? (
          <div className={styles.spinnerCenter}>
            <Spin size="large" />
          </div>
        ) : !hasData ? (
          <div className={styles.emptyCenter}>
            <Empty description={`No ${config.plural.toLowerCase()} data found`} />
          </div>
        ) : (
          <>
            {/* ── Charts row ──────────────────────────────────────────── */}
            <div className={styles.chartsRow}>
              {/* Bar chart — Performance ranking */}
              <Card
                className={styles.chartCard}
                title={
                  <div className={styles.cardTitle}>
                    <BulbOutlined style={{ color: config.color }} />
                    <span>Performance Ranking</span>
                    <Tooltip title="Items sorted best → worst. Yellow dashed line = pass threshold (60%).">
                      <InfoCircleOutlined className={styles.infoIcon} />
                    </Tooltip>
                  </div>
                }
              >
                <div className={styles.chartArea}>
                  <Column {...barConfig} />
                </div>
              </Card>

              {/* Line chart — Trend over time */}
              <Card
                className={styles.chartCard}
                title={
                  <div className={styles.cardTitle}>
                    <BulbOutlined style={{ color: config.color }} />
                    <span>Accuracy Trend Over Time</span>
                    <Tooltip title="Monthly average accuracy. Yellow dashed line = pass threshold (60%).">
                      <InfoCircleOutlined className={styles.infoIcon} />
                    </Tooltip>
                  </div>
                }
              >
                <div className={styles.chartArea}>
                  {trendData.length > 0 ? (
                    <Line {...trendConfig} />
                  ) : (
                    <Empty description="No trend data available" />
                  )}
                </div>
              </Card>
            </div>

            {/* ── Detailed Table ──────────────────────────────────────── */}
            <Card
              className={styles.tableCard}
              title={
                <div className={styles.cardTitle}>
                  <span>Detailed Breakdown — by {config.label}</span>
                  <Tooltip title="Click column headers to sort. Use the search box to filter rows.">
                    <InfoCircleOutlined className={styles.infoIcon} />
                  </Tooltip>
                </div>
              }
            >
              <AnalyticsTable
                items={data?.items ?? []}
                nameLabel={config.label}
                attribute={attribute}
                isLoading={loading}
              />
            </Card>
          </>
        )}
      </div>
    </PageLayout>
  );
};

export default AnalyticsPage;
